import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useCompany } from './CompanyContext';
import userService from '../services/api/userService';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const { employee, loading: companyLoading } = useCompany();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's effective permissions
  const fetchPermissions = useCallback(async () => {
    if (!employee?.id) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await userService.getPermissions();

      if (response.success) {
        const perms = response.data.permissions || [];
        setPermissions(perms);
      } else {
        setPermissions([]);
      }
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setError(err.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [employee?.id]);

  // Pre-compute a Set of permission codes for O(1) lookups.
  // Previously used useState Map + setTimeout for caching — caused render-phase side effects
  // and cascading re-renders (React error #185). Replaced with pure useMemo Set that only
  // recomputes when the permissions array changes (after fetchPermissions).
  const permissionSet = useMemo(() => {
    if (!Array.isArray(permissions)) return new Set();
    return new Set(permissions.map(perm =>
      typeof perm === 'string' ? perm : perm.code
    ));
  }, [permissions]);

  // Check if user has a specific permission — pure computation, no side effects
  const hasPermission = useCallback((permissionCode) => {
    if (!permissionCode || !employee?.id) return false;
    return permissionSet.has(permissionCode);
  }, [employee?.id, permissionSet]);

  // Check multiple permissions (returns object with permission: boolean)
  const hasPermissions = useCallback((permissionCodes) => {
    if (!Array.isArray(permissionCodes)) return {};

    return permissionCodes.reduce((acc, code) => {
      acc[code] = hasPermission(code);
      return acc;
    }, {});
  }, [hasPermission]);

  // Refresh permissions (useful after role changes)
  const refreshPermissions = () => {
    return fetchPermissions();
  };

  useEffect(() => {
    if (!companyLoading) {
      fetchPermissions();
    }
  }, [employee, companyLoading, fetchPermissions]);

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasPermissions,
    refreshPermissions,
    isLoaded: !loading && !companyLoading
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Safe version of usePermissions that returns defaults when outside provider
 * Useful for components that may render outside PermissionProvider (like App.js sidebar)
 * Returns hasPermission that always returns true (show all items) when outside provider
 */
export const usePermissionsSafe = () => {
  const context = useContext(PermissionContext);

  if (context === undefined) {
    // Return safe defaults - show all items when outside provider
    return {
      permissions: [],
      loading: false,
      error: null,
      hasPermission: () => true, // Allow all when no provider
      hasPermissions: () => ({}),
      refreshPermissions: () => Promise.resolve(),
      isLoaded: true,
      isOutsideProvider: true // Flag to indicate we're outside the provider
    };
  }

  return { ...context, isOutsideProvider: false };
};

export default PermissionContext;
